import { Button } from 'components/ui/button';
import { deleteDevice, getDevices } from 'core/API/gameMasterUserDevices';
import React, { useState, useEffect } from 'react';
import styles from './GamemasterUserDevices.module.scss';
import { Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Button as MuiButton, CircularProgress, Box } from '@mui/material';
import { Snackbar, Alert } from '@mui/material';
import { useQuery, useQueryClient } from 'react-query';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import CreateDeviceSettings from './CreateDeviceSettings';
import ShowDeviceSettings from './ShowDeviceSettings';
import EditDeviceSettings from './EditDeviceSettings';

interface GamemasterUserDevicesProps {
    onViewModeChange?: (mode: 'list' | 'create' | 'show' | 'edit') => void;
    canEdit?: boolean;
    canDelete?: boolean;
    canExecute?: boolean;
}

const GamemasterUserDevices: React.FC<GamemasterUserDevicesProps> = ({
    onViewModeChange,
    canEdit = false,
    canDelete = false,
    canExecute = false,
}) => {
    const data = localStorage.getItem('workplace-id');
    const workplaceId = Number(data);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deviceToDelete, setDeviceToDelete] = useState<any>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [viewMode, setViewMode] = useState<'list' | 'create' | 'show' | 'edit'>('list');
    const [selectedDevice, setSelectedDevice] = useState<any>(null);
    const [isExpanded, setIsExpanded] = useState(true);

    // Notify parent of initial viewMode
    useEffect(() => {
        onViewModeChange?.('list');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const queryClient = useQueryClient();

    // Use useQuery for devices
    const { data: devicesData, isLoading: isFetching, refetch: refetchDevices } = useQuery(
        ['userDevices', workplaceId],
        async () => {
            const data = await getDevices();
            if (data && data.items && Array.isArray(data.items)) {
                return data.items;
            }
            return [];
        },
        {
            enabled: !!workplaceId,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutes
        }
    );

    const devices = devicesData || [];

    const deviceDeleteHandler = (device: any) => {
        if (!canDelete) {
            return;
        }
        setDeviceToDelete(device);
        setIsDeleteDialogOpen(true);
    };

    const handleCancelDelete = () => {
        setIsDeleteDialogOpen(false);
        setDeviceToDelete(null);
    };


    const handleAddClick = () => {
        if (!canExecute) {
            return;
        }
        setSelectedDevice(null);
        setViewMode('create');
        onViewModeChange?.('create');
    };

    const handleBackClick = () => {
        setViewMode('list');
        setSelectedDevice(null);
        onViewModeChange?.('list');
    };

    const handleViewClick = (device: any) => {
        setSelectedDevice(device);
        setViewMode('show');
        onViewModeChange?.('show');
    };

    const handleEditClick = (device: any) => {
        if (!canEdit) {
            return;
        }
        setSelectedDevice(device);
        setViewMode('edit');
        onViewModeChange?.('edit');
    };

    const toggleAccordion = () => {
        setIsExpanded(!isExpanded);
    };

    const handleConfirmDelete = async () => {
        if (!deviceToDelete) return;
        
        setIsDeleting(true);
        try {
            await deleteDevice(deviceToDelete.id);
            // Invalidate query - useQuery will handle refetch automatically
            await queryClient.invalidateQueries(['userDevices', workplaceId]);
            setSnackbar({
                open: true,
                message: 'Device deleted successfully',
                severity: 'success',
            });
        } catch (error) {
            console.error('Error deleting device:', error);
            setSnackbar({
                open: true,
                message: 'Failed to delete device. Please try again.',
                severity: 'error',
            });
        } finally {
            setIsDeleteDialogOpen(false);
            setDeviceToDelete(null);
            setIsDeleting(false);
        }
    };

    const handleDeviceCreated = () => {
        setSnackbar({
            open: true,
            message: 'Device created successfully',
            severity: 'success',
        });
        setViewMode('list');
        onViewModeChange?.('list');
    };

    const handleDeviceUpdated = () => {
        setSnackbar({
            open: true,
            message: 'Device updated successfully',
            severity: 'success',
        });
        setViewMode('list');
        onViewModeChange?.('list');
    };

    // Devices are fetched via useQuery above - no need for separate useEffect

    const getDeviceTypeLabel = (identifier: string) => {
        const typeMap: { [key: string]: string } = {
            'ipad': 'iPad',
            'mobile': 'Mobile',
            'other': 'Other',
        };
        return typeMap[identifier?.toLowerCase()] || identifier || '-';
    };

    const renderDialogs = () => (
        <>
            {/* Delete Confirmation Dialog */}
            <Dialog
                open={isDeleteDialogOpen}
                onClose={handleCancelDelete}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                maxWidth="sm"
                fullWidth
                style={{ zIndex: 9999 }}
                PaperProps={{
                    style: {
                        backgroundColor: 'white',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        minWidth: '400px'
                    }
                }}
            >
                <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete the device{' '}
                        {deviceToDelete && <strong>&ldquo;{deviceToDelete.name}&rdquo;</strong>}? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={handleCancelDelete} style={{ color: 'black' }} disabled={isDeleting}>
                        Cancel
                    </MuiButton>
                    <MuiButton
                        style={{ backgroundColor: 'red', color: 'white' }}
                        onClick={handleConfirmDelete}
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </MuiButton>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );

    switch (viewMode) {
        case 'create':
            return (
                <>
                    <CreateDeviceSettings
                        workplaceId={workplaceId}
                        devices={devices}
                        handleBackClick={handleBackClick}
                        onDeviceCreated={handleDeviceCreated}
                    />
                    {renderDialogs()}
                </>
            );
        
        case 'edit':
            return selectedDevice ? (
                <>
                    <EditDeviceSettings
                        device={selectedDevice}
                        devices={devices}
                        workplaceId={workplaceId}
                        handleBackClick={handleBackClick}
                        onDeviceUpdated={handleDeviceUpdated}
                    />
                    {renderDialogs()}
                </>
            ) : null;
        
        case 'show':
            return selectedDevice ? (
                <>
                    <ShowDeviceSettings
                        device={selectedDevice}
                        handleBackClick={handleBackClick}
                    />
                    {renderDialogs()}
                </>
            ) : null;
        
        case 'list':
        default:
            return (
                <>
                    <div style={{ width: '100%', margin: '10px 0', overflow: 'hidden', fontFamily: 'var(--Gotham)' }}>
                        <div className={styles.accordionHeader}>
                            <button onClick={toggleAccordion} style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                                <h5>Devices</h5>
                            </button>
                            <Button 
                                className={styles.addButton} 
                                onClick={handleAddClick}
                                disabled={!canExecute || devices.length >= 5}
                                title={
                                    !canExecute
                                        ? "You do not have permission to add devices"
                                        : devices.length >= 5
                                            ? "Maximum of 5 devices allowed"
                                            : "Add new device"
                                }
                                style={{ 
                                    opacity: !canExecute || devices.length >= 5 ? 0.5 : 1, 
                                    cursor: !canExecute || devices.length >= 5 ? 'not-allowed' : 'pointer',
                                    marginLeft: 'auto'
                                }}
                            >
                                Add
                            </Button>
                        </div>
                        <div
                            className={`${styles.accordionContent} ${isExpanded ? styles.expanded : styles.collapsed}`}
                        >
                            <div className={styles.content}>
                                <div className={styles.deviceCard}>
                                    {isFetching ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                                            <CircularProgress size={25} />
                                        </Box>
                                    ) : devices.length > 0 ? (
                                        <div className={styles.tableContainer}>
                                            <table className={styles.deviceTable}>
                                                <thead>
                                                    <tr>
                                                        <th>Device Number</th>
                                                        <th>Device Name</th>
                                                        <th>Device Type</th>
                                                        <th>Device Log In</th>
                                                        <th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>View</th>
                                                        <th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Delete</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {devices.map((device: any, index: number) => (
                                                        <tr key={device.id}>
                                                            <td>{index + 1}</td>
                                                            <td>{device.name || '-'}</td>
                                                            <td>{getDeviceTypeLabel(device.identifier)}</td>
                                                            <td>{device.username || '-'}</td>
                                                            <td>
                                                                <button
                                                                    className={styles.iconButton}
                                                                    onClick={() => handleViewClick(device)}
                                                                    title="View Device"
                                                                >
                                                                    <Visibility className={styles.editIcon} />
                                                                </button>
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className={styles.iconButton}
                                                                    onClick={() => deviceDeleteHandler(device)}
                                                                    title={canDelete ? "Delete Device" : "You do not have permission to delete"}
                                                                    disabled={!canDelete}
                                                                    style={{
                                                                        opacity: canDelete ? 1 : 0.5,
                                                                        cursor: canDelete ? 'pointer' : 'not-allowed'
                                                                    }}
                                                                >
                                                                    <Delete className={styles.deleteIcon} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className={styles.noDataMessage}>No devices found.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {renderDialogs()}
                </>
            );
    }
};

export default GamemasterUserDevices;